import { jwtDecode } from 'jwt-decode';
import HttpService from './HttpService';

class AuthValidationService {
    constructor() {
        this.httpService = new HttpService();
        this.refreshPromise = null; // Para evitar múltiples llamadas simultáneas
        this.lastValidation = null; // Cache de la última validación
        this.validationTimeout = 30000; // 30 segundos de cache
    }

    /**
     * Valida si existe un token válido en localStorage
     */
    hasValidToken() {
        const token = localStorage.getItem('user-token');
        if (!token) {
            return false;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            
            // Verificar si el token ha expirado
            if (decodedToken.exp < currentTime) {
                this.clearTokens();
                return false;
            }
            
            return true;
        } catch (error) {
            this.clearTokens();
            return false;
        }
    }

    /**
     * Limpia todos los tokens del localStorage
     * @param {boolean} preserveAdminToken - Si es true, solo limpia user-token
     */
    clearTokens(preserveAdminToken = false) {
        localStorage.removeItem('user-token');
        if (!preserveAdminToken) {
            localStorage.removeItem('admin-token');
        }
    }

    /**
     * Verifica si la validación en cache es válida
     */
    isCachedValidationValid() {
        if (!this.lastValidation) return false;
        
        const now = Date.now();
        const validationAge = now - this.lastValidation.timestamp;
        
        return validationAge < this.validationTimeout && this.lastValidation.result.isValid;
    }

    /**
     * Valida y refresca la sesión actual usando el endpoint refresh-token
     */
    async validateAndRefreshSession() {
        // Si no hay token, retornar inmediatamente
        if (!this.hasValidToken()) {
            this.lastValidation = null;
            this.refreshPromise = null;
            return { isValid: false, reason: 'no-token' };
        }

        // Si hay una validación reciente en cache, usarla
        if (this.isCachedValidationValid()) {
            return this.lastValidation.result;
        }

        // Si ya hay una llamada en progreso, esperar a que termine
        if (this.refreshPromise) {
            try {
                return await this.refreshPromise;
            } catch (error) {
                this.refreshPromise = null;
                return { isValid: false, reason: 'validation-error' };
            }
        }

        // Crear nueva promesa de validación
        this.refreshPromise = this._performValidation();

        try {
            const result = await this.refreshPromise;
            
            // Guardar en cache solo si es exitoso
            if (result.isValid) {
                this.lastValidation = {
                    timestamp: Date.now(),
                    result: result
                };
            } else {
                this.lastValidation = null;
            }
            
            return result;
        } catch (error) {
            return { isValid: false, reason: 'validation-error', error };
        } finally {
            // Limpiar la promesa
            this.refreshPromise = null;
        }
    }

    /**
     * Realiza la validación real con el servidor
     */
    async _performValidation() {
        try {            
            // Llamada al endpoint que valida y refresca el token automáticamente
            const response = await this.httpService.postData({}, 'user/refresh-token', 'user-token');
            
            if (response && response.success) {
                // Actualizar tokens del servidor
                if (response.token) {
                    localStorage.setItem('user-token', response.token);
                }
                
                if (response.admin_token) {
                    localStorage.setItem('admin-token', response.admin_token);
                }
                
                return { 
                    isValid: true, 
                    user: response.user,
                    tokenRefreshed: !!response.token
                };
            } else if (response && (response.error === 'Unauthenticated' || response.message === 'Unauthenticated')) {
                this.clearTokens();
                return { isValid: false, reason: 'unauthorized' };
            } else if (response && response.error) {
                this.clearTokens();
                return { isValid: false, reason: 'invalid-session' };
            } else {
                return { isValid: false, reason: 'invalid-response' };
            }
        } catch (error) {
            // Para errores de red, mantener el token pero reportar como no válido
            return { isValid: false, reason: 'network-error', error };
        }
    }

    /**
     * Método de compatibilidad - usa validateAndRefreshSession
     */
    async validateSession() {
        return await this.validateAndRefreshSession();
    }

    /**
     * Obtiene información del usuario actual sin refrescar el token
     */
    async getUserInfo() {
        if (!this.hasValidToken()) {
            return { success: false, reason: 'no-token' };
        }

        try {
            const response = await this.httpService.getData('user/profile', 'user-token');
            
            if (response && response.success && response.user) {
                return { success: true, user: response.user };
            } else {
                return { success: false, reason: 'invalid-response', response };
            }
        } catch (error) {
            return { success: false, reason: 'network-error', error };
        }
    }

    /**
     * Validación rápida basada solo en el token JWT (sin llamada al servidor)
     */
    validateTokenLocally() {
        const tokenInfo = this.getTokenInfo();
        
        if (!tokenInfo) {
            return { isValid: false, reason: 'no-token' };
        }
        
        if (tokenInfo.isExpired) {
            this.clearTokens();
            return { isValid: false, reason: 'token-expired' };
        }
        
        return { isValid: true, tokenInfo };
    }

    /**
     * Obtiene información del token sin validar en el servidor
     */
    getTokenInfo() {
        const token = localStorage.getItem('user-token');
        if (!token) {
            return null;
        }

        try {
            const decodedToken = jwtDecode(token);
            return {
                userId: decodedToken.sub,
                issuer: decodedToken.iss,
                issuedAt: new Date(decodedToken.iat * 1000),
                expiresAt: new Date(decodedToken.exp * 1000),
                isExpired: decodedToken.exp < (Date.now() / 1000)
            };
        } catch (error) {
            return null;
        }
    }

    /**
     * Redirige al login
     */
    redirectToLogin() {
        window.location.href = '/user/login';
    }

    /**
     * Verifica si el usuario está autenticado
     */
    isAuthenticated() {
        return this.hasValidToken();
    }
}

export default new AuthValidationService();